import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { isAxiosError } from "axios";
import { companiesApi, marketDataStalenessAPI, orderRecommendationApi } from "@/services/api";
import { formatDate, formatPrice } from "@/utils/formatters";
import { isGlobalCountryCode } from "@/utils/country";
import type { LivePrice } from "@/types/client";
import type { PriceFmt } from "../utils";

type CompanyOption = { id: number; name: string; symbol: string; country_code: string | null };
type PendingRow = Awaited<ReturnType<typeof orderRecommendationApi.getCompanyPending>>[number];
type StrategyChoice = "CORE" | "LHF" | "BOTH";
type ExecutionType = "limit" | "market";
type Priority = "high" | "medium" | "low";
type DirectionConflict = { strategy_group: "CORE" | "LHF"; existing_order_type: 1 | 2 };

// Selected-state colours per priority — subtle chips matching the page palette.
const PRIORITY_SELECTED_SX: Record<Priority, { color: string; backgroundColor: string }> = {
  high: { color: "var(--warn)", backgroundColor: "var(--warn-soft)" },
  medium: { color: "#1d4ed8", backgroundColor: "var(--accent-soft)" },
  low: { color: "#475569", backgroundColor: "var(--surface-2)" },
};

interface QuickDecisionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save with a human-readable summary (e.g. "Updated CORE · Created LHF"). */
  onSaved: (message: string) => void;
}

// Mirrors the Edit Orders execution cell defaults: Limit | Market primary
// toggle, %-of-volume as the standing method with 5% default.
const DEFAULT_VOLUME_PCT = "5";

// A live price is usable only when it was updated TODAY — the market-data
// feed can lag, and a stale LTP must neither read as current nor seed the
// 1.02× default limit price.
const isLtpFromToday = (timestamp: string | null): boolean => {
  if (!timestamp) return false;
  const updated = new Date(timestamp);
  const now = new Date();
  return (
    updated.getFullYear() === now.getFullYear() &&
    updated.getMonth() === now.getMonth() &&
    updated.getDate() === now.getDate()
  );
};

// Cap the rendered options — the full securities master is ~5,000 rows and an
// unvirtualized MUI listbox freezes if asked to render them all. Matching is
// done against "name symbol" so typing either finds the company.
const filterCompanyOptions = createFilterOptions<CompanyOption>({
  limit: 50,
  stringify: option => `${option.name} ${option.symbol}`,
});

const fieldLabelSx = {
  fontSize: 11,
  fontWeight: 600,
  color: "var(--text-dim)",
  textTransform: "uppercase" as const,
  letterSpacing: 0.4,
  mb: 0.5,
};

/**
 * Compact one-line summary of an existing active tracker, shown in the
 * "current status" panel once a company is selected. ``fmtPrice`` renders money in
 * the company's currency — "₹" for Domestic, the code in brackets for Global.
 */
const pendingRowSummary = (row: PendingRow, fmtPrice: PriceFmt): string => {
  const parts: string[] = [];
  parts.push(row.execution_type === "market" ? "Market" : "Limit");
  if (row.execution_type !== "market" && row.limit_price != null) {
    parts.push(fmtPrice(row.limit_price));
  }
  if (row.execution_method === "pct_volume" && row.volume_pct != null) {
    parts.push(`${parseFloat(row.volume_pct).toFixed(0)}% vol`);
  } else if (row.execution_method === "vwap") {
    parts.push("VWAP");
  }
  if (row.cio_target_pct != null) parts.push(`CIO ${parseFloat(row.cio_target_pct).toFixed(2)}%`);
  if (row.priority) parts.push(`${row.priority.charAt(0).toUpperCase()}${row.priority.slice(1)} priority`);
  if (row.cio_decision) parts.push("Continue");
  return parts.join(" · ");
};

const QuickDecisionDialog: React.FC<QuickDecisionDialogProps> = ({ open, onClose, onSaved }) => {
  // Company options are fetched once per mount, lazily on first open.
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const hasFetchedCompaniesRef = useRef(false);
  const [company, setCompany] = useState<CompanyOption | null>(null);

  // Current pending-order status for the selected company.
  const [pendingRows, setPendingRows] = useState<PendingRow[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Live market price of the selected company — same source the order
  // placement form uses (marketDataStalenessAPI.getLivePrice → /api/live-price/).
  // Powers the LTP hint and the 1.02×LTP default limit price on create.
  const [livePrice, setLivePrice] = useState<LivePrice | null>(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [limitPriceDefaulted, setLimitPriceDefaulted] = useState(false);
  const userEditedLimitPrice = useRef(false);

  // Form fields.
  const [orderType, setOrderType] = useState<1 | 2>(1);
  const [strategyGroup, setStrategyGroup] = useState<StrategyChoice>("BOTH");
  const [executionType, setExecutionType] = useState<ExecutionType>("limit");
  const [volumePct, setVolumePct] = useState(DEFAULT_VOLUME_PCT);
  const [limitPrice, setLimitPrice] = useState("");
  const [cioTargetPct, setCioTargetPct] = useState("");
  const [comment, setComment] = useState("");
  // Direct CIO decisions default to high priority (the tracker default is low).
  const [priority, setPriority] = useState<Priority>("high");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Opposite-direction trackers need explicit user permission before they are
  // converted (e.g. an active BUY when a SELL decision is being saved).
  const [convertPrompt, setConvertPrompt] = useState<DirectionConflict[] | null>(null);

  // Signature (tracker ids) of the entry the form last prefilled from. Prefill
  // and reset fire only when this changes, so retyping is never wiped by a
  // re-render, but stale values can never survive a company/selection switch.
  const prefillSignatureRef = useRef<string>("none");

  // Reset the decision inputs to their defaults (company/direction/strategy
  // are the user's scope selection and are managed separately).
  const resetDecisionFields = useCallback(() => {
    setExecutionType("limit");
    setVolumePct(DEFAULT_VOLUME_PCT);
    setLimitPrice("");
    setCioTargetPct("");
    setComment("");
    setPriority("high");
  }, []);

  const handleCompanyChange = useCallback((selected: CompanyOption | null) => {
    setCompany(selected);
    // Global companies are traded by the Core (SGS) strategy only — LHF is domestic —
    // so Global defaults to Core while Domestic keeps the Both default.
    setStrategyGroup(isGlobalCountryCode(selected?.country_code) ? "CORE" : "BOTH");
    // Clear the previous company's rows immediately — waiting for the new
    // fetch would let stale rows prefill (or keep) the wrong values.
    setPendingRows([]);
    setLivePrice(null);
    setLimitPriceDefaulted(false);
    userEditedLimitPrice.current = false;
    resetDecisionFields();
    setError(null);
    prefillSignatureRef.current = "none";
  }, [resetDecisionFields]);

  useEffect(() => {
    if (!open || hasFetchedCompaniesRef.current) return;
    let cancelled = false;
    setCompaniesLoading(true);
    companiesApi
      .getCompanyOptions()
      .then(options => {
        if (cancelled) return;
        // Marked only once the list is in hand, so closing mid-fetch or a failure refetches on reopen.
        hasFetchedCompaniesRef.current = true;
        setCompanies(options);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load companies. Close and try again.");
      })
      .finally(() => {
        if (!cancelled) setCompaniesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Fetch the minimal pending-order status whenever the company changes.
  useEffect(() => {
    if (!company) {
      setPendingRows([]);
      return;
    }
    const controller = new AbortController();
    setPendingLoading(true);
    orderRecommendationApi
      .getCompanyPending(company.id, { signal: controller.signal })
      .then(rows => setPendingRows(rows))
      .catch(() => {
        if (!controller.signal.aborted) setPendingRows([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPendingLoading(false);
      });
    return () => controller.abort();
  }, [company]);

  // Live price fetch — same flow as the Buy/Sell order forms.
  useEffect(() => {
    if (!company) {
      setLivePrice(null);
      return;
    }
    let cancelled = false;
    setLivePriceLoading(true);
    marketDataStalenessAPI
      .getLivePrice(company.id)
      .then(data => {
        if (!cancelled) setLivePrice(data);
      })
      .catch(() => {
        // A populated object with a null ltp is what renders "LTP not found"; null would
        // render nothing at all.
        if (!cancelled) {
          setLivePrice({
            company_id: company.id,
            ltp: null,
            currency_code: null,
            exchange_code: null,
            ltp_timestamp: null,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLivePriceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [company]);

  // The existing trackers this decision would touch, given the current selection.
  const matchingRows = useMemo(() => {
    const groups = strategyGroup === "BOTH" ? ["CORE", "LHF"] : [strategyGroup];
    return pendingRows.filter(
      row => row.order_type === orderType && row.strategy_group != null && groups.includes(row.strategy_group)
    );
  }, [pendingRows, orderType, strategyGroup]);

  // Groups whose only active tracker runs in the opposite direction — converting
  // those requires the user's explicit permission. A group that also has a
  // same-direction tracker never conflicts (that one is updated instead).
  const directionConflicts = useMemo<DirectionConflict[]>(() => {
    const groups: Array<"CORE" | "LHF"> =
      strategyGroup === "BOTH" ? ["CORE", "LHF"] : [strategyGroup];
    return groups.flatMap(group => {
      const groupRows = pendingRows.filter(row => row.strategy_group === group);
      if (groupRows.some(row => row.order_type === orderType)) return [];
      const opposite = groupRows.find(row => row.order_type !== orderType);
      return opposite
        ? [{ strategy_group: group, existing_order_type: opposite.order_type }]
        : [];
    });
  }, [pendingRows, orderType, strategyGroup]);

  // Prefill from the matched tracker whenever the matched entry itself changes.
  // Signature transitions and their outcomes:
  //   none → match : prefill from the entry being edited
  //   match → match: prefill from the newly matched entry
  //   match → none : reset — a buy's values must not linger on a sell decision
  //   none → none  : do nothing — never wipe values the user is typing
  useEffect(() => {
    const matchSignature = matchingRows.map(row => row.id).join(",") || "none";
    if (prefillSignatureRef.current === matchSignature) return;
    prefillSignatureRef.current = matchSignature;

    userEditedLimitPrice.current = false;
    setLimitPriceDefaulted(false);

    const match = matchingRows[0];
    if (!match) {
      resetDecisionFields();
      return;
    }
    setExecutionType(match.execution_type === "market" ? "market" : "limit");
    setVolumePct(match.volume_pct != null ? parseFloat(match.volume_pct).toString() : DEFAULT_VOLUME_PCT);
    setLimitPrice(match.limit_price != null ? parseFloat(match.limit_price).toFixed(2) : "");
    setCioTargetPct(match.cio_target_pct != null ? parseFloat(match.cio_target_pct).toFixed(2) : "");
    setComment(match.comment ?? "");
    setPriority(match.priority);
  }, [matchingRows, resetDecisionFields]);

  // Global rows label every price with its currency code (e.g. "7.45 (GBP)") instead of
  // the domestic "₹". The code comes from the live feed, which the backend publishes in
  // the canonical major unit — the same currency the recommendations table shows.
  const isGlobal = isGlobalCountryCode(company?.country_code);
  const currencyCode = livePrice?.currency_code ?? null;
  const fmtPrice: PriceFmt = (value) => formatPrice(value, currencyCode, isGlobal);
  const priceInputPrefix = isGlobal ? (currencyCode ?? "") : "₹";

  // Today's LTP, or null when missing/outdated. Everything downstream (hint,
  // default price) keys off this so a stale feed price is never surfaced.
  const freshLtp =
    livePrice?.ltp != null && isLtpFromToday(livePrice.ltp_timestamp) ? livePrice.ltp : null;
  const ltpOutdated = livePrice?.ltp != null && !isLtpFromToday(livePrice.ltp_timestamp);

  // Default the limit price to 1.02 × LTP for NEW entries — the same default
  // the Edit Orders table applies on create rows. Never fires when editing an
  // existing entry (its own price prefills), after the user typed a price, or
  // when the feed price is not from today.
  useEffect(() => {
    if (freshLtp == null || userEditedLimitPrice.current) return;
    if (matchingRows.length > 0) return;
    setLimitPrice((freshLtp * 1.02).toFixed(2));
    setLimitPriceDefaulted(true);
  }, [freshLtp, matchingRows]);

  // x% of volume is the standing execution method (as in Edit Orders), so the
  // volume % is always required and must sit in the 1–100 range.
  const parsedVolumePct = parseFloat(volumePct);
  const volumePctInvalid =
    volumePct.trim() === "" || isNaN(parsedVolumePct) || parsedVolumePct <= 0 || parsedVolumePct > 100;

  const cioTargetInvalid =
    cioTargetPct.trim() === "" || isNaN(parseFloat(cioTargetPct)) || parseFloat(cioTargetPct) < 0;

  // Limit price is optional — invalid only when filled with a non-positive number.
  const limitPriceInvalid =
    limitPrice.trim() !== "" && (isNaN(parseFloat(limitPrice)) || parseFloat(limitPrice) <= 0);

  // pendingLoading also blocks save: prefill and conflict detection depend on
  // the status fetch, so saving before it lands would act on unknown state.
  const canSave =
    company != null &&
    !cioTargetInvalid &&
    !volumePctInvalid &&
    !limitPriceInvalid &&
    !saving &&
    !pendingLoading;

  const resetForm = useCallback(() => {
    setCompany(null);
    setPendingRows([]);
    setLivePrice(null);
    setLimitPriceDefaulted(false);
    userEditedLimitPrice.current = false;
    setOrderType(1);
    setStrategyGroup("BOTH");
    resetDecisionFields();
    setError(null);
    setConvertPrompt(null);
    prefillSignatureRef.current = "none";
  }, [resetDecisionFields]);

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleSave = async (convertExisting = false) => {
    if (!company || !canSave) return;
    // Opposite-direction trackers need the user's permission before conversion.
    if (!convertExisting && directionConflicts.length > 0) {
      setConvertPrompt(directionConflicts);
      return;
    }
    // Captured up front so the summary reflects the direction that was saved,
    // even if orderType changes while the request is in flight.
    const savedOrderType = orderType;
    setSaving(true);
    setError(null);
    try {
      const response = await orderRecommendationApi.quickDecision({
        company: company.id,
        order_type: savedOrderType,
        strategy_group: strategyGroup,
        execution_type: executionType,
        execution_method: "pct_volume",
        volume_pct: parseFloat(volumePct).toFixed(2),
        limit_price:
          executionType === "limit" && limitPrice.trim() !== ""
            ? parseFloat(limitPrice).toFixed(2)
            : null,
        cio_target_pct: parseFloat(cioTargetPct).toFixed(2),
        comment: comment.trim(),
        priority,
        convert_existing: convertExisting,
      });
      const actionLabel = (action: "created" | "updated" | "converted") =>
        action === "created"
          ? "Created"
          : action === "converted"
            ? `Changed to ${savedOrderType === 1 ? "BUY" : "SELL"}:`
            : "Updated";
      const summary = response.results
        .map(r => `${actionLabel(r.action)} ${r.strategy_group}`)
        .join(" · ");
      resetForm();
      onSaved(`${company.symbol}: ${summary}`);
      onClose();
    } catch (err) {
      // Fallback for stale status data: the backend re-checks direction
      // conflicts itself and reports them with a 409.
      if (
        isAxiosError(err) &&
        err.response?.status === 409 &&
        Array.isArray(err.response.data?.conflicts)
      ) {
        setConvertPrompt(err.response.data.conflicts as DirectionConflict[]);
      } else {
        setError("Failed to save the decision. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <BoltOutlinedIcon sx={{ color: "#f59e0b" }} fontSize="small" />
        <span style={{ fontSize: 18, fontWeight: 700 }}>Quick Decision</span>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.25}>
          <div>
            <Typography sx={fieldLabelSx}>Company</Typography>
            <Autocomplete
              options={companies}
              value={company}
              loading={companiesLoading}
              onChange={(_, selected) => handleCompanyChange(selected)}
              getOptionLabel={c => `${c.name} (${c.symbol})`}
              filterOptions={filterCompanyOptions}
              autoHighlight
              noOptionsText={
                companiesLoading
                  ? "Loading companies…"
                  : companies.length === 0
                    ? "Companies failed to load — close and reopen the form"
                    : "No matching company"
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}{" "}
                  <span style={{ color: "var(--text-faint)", marginLeft: 6 }}>({option.symbol})</span>
                </li>
              )}
              isOptionEqualToValue={(c, v) => c.id === v?.id}
              renderInput={params => (
                <TextField
                  {...params}
                  placeholder="Search company…"
                  size="small"
                  autoFocus
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {companiesLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          {/* Current status — appears as soon as a company is chosen. */}
          {company && (
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "10px 12px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <Typography sx={{ ...fieldLabelSx, mb: 0.75 }}>Current pending orders</Typography>
                <Typography
                  sx={{ fontSize: 12.5, fontWeight: 600, color: ltpOutdated ? "#b45309" : "#334155" }}
                >
                  {livePriceLoading
                    ? "Fetching LTP…"
                    : freshLtp != null
                      ? `LTP ${fmtPrice(freshLtp)}`
                      : ltpOutdated
                        ? `LTP outdated (${formatDate(livePrice!.ltp_timestamp!)}) — not shown`
                        : livePrice != null
                          ? "LTP not found"
                          : ""}
                </Typography>
              </div>
              {pendingLoading ? (
                <Typography sx={{ fontSize: 13, color: "var(--text-faint)" }}>Checking…</Typography>
              ) : pendingRows.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: "var(--text-faint)" }}>
                  None — saving will create a new entry.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {pendingRows.map(row => (
                    <div key={row.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Chip
                        label={row.order_type === 1 ? "BUY" : "SELL"}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 700,
                          color: row.order_type === 1 ? "#15803d" : "#b91c1c",
                          backgroundColor: row.order_type === 1 ? "#dcfce7" : "#fee2e2",
                        }}
                      />
                      <Chip
                        label={row.strategy_group ?? "—"}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                      />
                      <Typography sx={{ fontSize: 13, color: "#334155" }}>
                        {pendingRowSummary(row, fmtPrice)}
                      </Typography>
                    </div>
                  ))}
                </Stack>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>
              <Typography sx={fieldLabelSx}>Direction</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={orderType}
                onChange={(_, v) => v != null && setOrderType(v)}
              >
                <ToggleButton
                  value={1}
                  sx={{ px: 2.5, fontWeight: 700, "&.Mui-selected": { color: "var(--pos)", backgroundColor: "var(--pos-soft)" } }}
                >
                  Buy
                </ToggleButton>
                <ToggleButton
                  value={2}
                  sx={{ px: 2.5, fontWeight: 700, "&.Mui-selected": { color: "var(--neg)", backgroundColor: "var(--neg-soft)" } }}
                >
                  Sell
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div>
              <Typography sx={fieldLabelSx}>Strategy</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={strategyGroup}
                onChange={(_, v) => v != null && setStrategyGroup(v)}
              >
                <ToggleButton value="CORE" sx={{ px: 2, fontWeight: 600 }}>Core</ToggleButton>
                <ToggleButton value="LHF" sx={{ px: 2, fontWeight: 600 }}>LHF</ToggleButton>
                <ToggleButton value="BOTH" sx={{ px: 2, fontWeight: 600 }}>Both</ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div>
              <Typography sx={fieldLabelSx}>Priority</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={priority}
                onChange={(_, v) => v != null && setPriority(v)}
              >
                {(["high", "medium", "low"] as const).map(level => (
                  <ToggleButton
                    key={level}
                    value={level}
                    sx={{
                      px: 1.75,
                      fontWeight: 600,
                      "&.Mui-selected": PRIORITY_SELECTED_SX[level],
                    }}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: 16, alignItems: "flex-end" }}>
            <div>
              <Typography sx={fieldLabelSx}>Execution</Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={executionType}
                onChange={(_, v) => v != null && setExecutionType(v)}
              >
                <ToggleButton
                  value="limit"
                  sx={{ px: 2, fontWeight: 600, "&.Mui-selected": { color: "#1d4ed8", backgroundColor: "var(--accent-soft)" } }}
                >
                  Limit
                </ToggleButton>
                <ToggleButton
                  value="market"
                  sx={{ px: 2, fontWeight: 600, "&.Mui-selected": { color: "#7c3aed", backgroundColor: "#ede9fe" } }}
                >
                  Market
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            {executionType === "limit" && (
              <div style={{ flex: 1 }}>
                <Typography sx={fieldLabelSx}>Limit Price</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  placeholder="Blank keeps current"
                  value={limitPrice}
                  onChange={e => {
                    userEditedLimitPrice.current = true;
                    setLimitPriceDefaulted(false);
                    setLimitPrice(e.target.value);
                  }}
                  onBlur={e => {
                    const parsed = parseFloat(e.target.value);
                    if (!isNaN(parsed)) setLimitPrice(parsed.toFixed(2));
                  }}
                  error={limitPriceInvalid}
                  inputProps={{ min: 0.01, step: 0.05 }}
                  InputProps={{
                    startAdornment: priceInputPrefix ? (
                      <InputAdornment position="start" sx={{ "& p": { fontSize: 12.5 } }}>
                        {priceInputPrefix}
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </div>
            )}
            <div style={{ width: 110 }}>
              <Typography sx={fieldLabelSx}>x% of Volume</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="1–100"
                value={volumePct}
                onChange={e => setVolumePct(e.target.value)}
                // Rounded on blur, as Limit Price and CIO Target % are: volume_pct is a
                // 2-decimal column, and volumePctInvalid only checks the 1–100 range — so
                // a typed 5.555 passes the client guard and is rejected by the serializer.
                onBlur={e => {
                  const parsed = parseFloat(e.target.value);
                  if (!isNaN(parsed)) setVolumePct(parsed.toFixed(2));
                }}
                error={volumePct.trim() !== "" && volumePctInvalid}
                inputProps={{ min: 1, max: 100, step: 0.01 }}
              />
            </div>
          </div>

          {/* LTP hint — mirrors the Buy/Sell order forms' live-price hint.
              Shown only for today's price; a stale feed price is never surfaced. */}
          {company && executionType === "limit" && freshLtp != null && (
            <Typography sx={{ fontSize: 12, color: limitPriceDefaulted ? "#1d4ed8" : "#64748b", mt: -1.5 }}>
              {limitPriceDefaulted
                ? `Limit price defaulted to 1.02 × LTP (${fmtPrice(freshLtp)} → ${fmtPrice(freshLtp * 1.02)})`
                : `LTP ${fmtPrice(freshLtp)} · 1.02× = ${fmtPrice(freshLtp * 1.02)}`}
            </Typography>
          )}

          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <Typography sx={fieldLabelSx}>CIO Target %</Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="e.g. 2.50"
                value={cioTargetPct}
                onChange={e => setCioTargetPct(e.target.value)}
                onBlur={e => {
                  const parsed = parseFloat(e.target.value);
                  if (!isNaN(parsed)) setCioTargetPct(parsed.toFixed(2));
                }}
                error={cioTargetPct.trim() !== "" && cioTargetInvalid}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </div>
          </div>

          <div>
            <Typography sx={fieldLabelSx}>Comment</Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              placeholder="Optional note…"
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>

          {company && !pendingLoading && (
            <Typography
              sx={{ fontSize: 12.5, color: directionConflicts.length > 0 ? "#b45309" : "#64748b" }}
            >
              {directionConflicts.length > 0
                ? `${directionConflicts.map(c => c.strategy_group).join(" and ")} currently ${
                    directionConflicts.length > 1 ? "have" : "has"
                  } an active ${directionConflicts[0].existing_order_type === 1 ? "BUY" : "SELL"} order — saving will ask your permission to change it to ${
                    orderType === 1 ? "BUY" : "SELL"
                  }.`
                : matchingRows.length > 0
                  ? `Saving will update the existing ${matchingRows
                      .map(r => r.strategy_group)
                      .join(" and ")} ${matchingRows.length > 1 ? "entries" : "entry"}${
                      strategyGroup === "BOTH" && matchingRows.length === 1 ? " and create the other" : ""
                    }.`
                  : `Saving will create ${strategyGroup === "BOTH" ? "new CORE and LHF entries" : `a new ${strategyGroup} entry`}.`}
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => handleSave()} disabled={!canSave}>
          {saving ? "Saving…" : "Save Decision"}
        </Button>
      </DialogActions>

      {/* Permission prompt: an active tracker runs in the opposite side. */}
      <Dialog open={convertPrompt !== null} onClose={() => setConvertPrompt(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHorizOutlinedIcon sx={{ color: "#f59e0b" }} fontSize="small" />
          Change order side?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#334155", mb: 1.5 }}>
            {company?.name} already has {convertPrompt && convertPrompt.length > 1 ? "active orders" : "an active order"} in
            the opposite side:
          </Typography>
          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            {(convertPrompt ?? []).map(conflict => (
              <div key={conflict.strategy_group} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Chip
                  label={conflict.strategy_group}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                  <span style={{ color: conflict.existing_order_type === 1 ? "#15803d" : "#b91c1c" }}>
                    {conflict.existing_order_type === 1 ? "BUY" : "SELL"}
                  </span>
                  <span style={{ color: "var(--text-faint)", margin: "0 6px" }}>→</span>
                  <span style={{ color: orderType === 1 ? "#15803d" : "#b91c1c" }}>
                    {orderType === 1 ? "BUY" : "SELL"}
                  </span>
                </Typography>
              </div>
            ))}
          </Stack>
          <Typography sx={{ fontSize: 13, color: "var(--text-dim)" }}>
            The existing {convertPrompt && convertPrompt.length > 1 ? "entries" : "entry"} will be converted to{" "}
            {orderType === 1 ? "BUY" : "SELL"} — the previous limit price and last-order target will be cleared.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertPrompt(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving}
            onClick={() => {
              setConvertPrompt(null);
              void handleSave(true);
            }}
          >
            Change to {orderType === 1 ? "Buy" : "Sell"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default QuickDecisionDialog;
